import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

export default class Menu {
  constructor() {
    this.screens = {
      isTablet: window.innerWidth < 1024
    };
    this.header = document.querySelector('.js-header');
    if (!this.header) return;

    this.clearHeader = this.header.cloneNode(true);

    this.init();
  }

  init = () => {
    if (this.header.matches('.is-compact')) {
      this.initScrollEvent();
    } else {
      this.initVariables();
      if (this.screens.isTablet) {
        this.initMobileEvents();
      } else {
        this.initDesktopEvents();
      }
      this.initScrollEvent();
    }
  }

  destroy = () => {
    if (!this.header) return;
    
    ScrollTrigger.getById('headerTriger').kill(); 
    this.header.replaceWith(this.clearHeader);
    this.header = document.querySelector('.js-header');
    this.clearHeader = this.header.cloneNode(true);
  }

  initVariables = () => {
    this.menu = this.header.querySelector('.js-menu');
    this.hoverboard = this.header.querySelector('.js-menu-hoverboard');
    this.menuData = {
      burger: this.header.querySelector('.js-menu-burger'),
      links: [...this.menu.querySelectorAll('.js-menu-link')].map(link => ({node: link})),
      dropdowns: [...this.header.querySelectorAll('.js-menu-dropdown')].map(dropdown => ({node: dropdown, index: Number(dropdown.getAttribute('data-submenu'))})),
      navContainer: this.header.querySelector('.js-nav-container'),
      submenuContainer: this.header.querySelector('.js-menu-container'),
      submenus: [...this.header.querySelectorAll('.js-submenu')].map(submenu=> {
        return {
          node: submenu,
          isActive: Number(submenu.getAttribute('data-submenu')) == 1 ? true : false,
          index: Number(submenu.getAttribute('data-submenu')),
          buttonsWidth: submenu.querySelector('.js-submenu-buttons').getBoundingClientRect().width,
          height: submenu.getBoundingClientRect().height,
          buttonsContainer: submenu.querySelector('.js-submenu-buttons'),
          buttons: [...submenu.querySelectorAll('.js-submenu-button')].map(button => {
            return {
              node: button,
              isActive: Number(button.getAttribute('data-links')) == 1 ? true : false,
              index: Number(button.getAttribute('data-links')),
              links: {
                backButton: submenu.querySelector(`.js-submenu-links[data-links="${button.getAttribute('data-links')}"]`).querySelector('.js-links-back'),
                node: submenu.querySelector(`.js-submenu-links[data-links="${button.getAttribute('data-links')}"]`),
                height: submenu.querySelector(`.js-submenu-links[data-links="${button.getAttribute('data-links')}"]`).getBoundingClientRect().height,
                width: submenu.querySelector(`.js-submenu-links[data-links="${button.getAttribute('data-links')}"]`).getBoundingClientRect().width,
              }
            }
          }),
        }
      })
    }

    this.langs = this.header.querySelector('.js-langs') ? {
      container: this.header.querySelector('.js-langs'),
      button: this.header.querySelector('.js-langs').querySelector('.js-menu-button'),
      list: this.header.querySelector('.js-langs-list'),
      back: this.header.querySelector('.js-langs-back'),
    } : null;
    this.login = this.header.querySelector('.js-login');
    this.closeButtons = [...this.header.querySelectorAll('.js-menu-close')];
  }

  initDesktopEvents = () => {
    // For menu link
    this.menuData.links.forEach(link => {
      link.node.addEventListener('mouseenter', (e) => {
        this.moveHoverboard(e.target.querySelector('.js-menu-button'));
      });
      link.node.addEventListener('mouseleave', () => {
        this.hideHoverboard();
      });
    });

    // For langs
    if (this.langs) {
      this.langs.container.addEventListener('mouseenter', () => {
        this.toggleLangs('show');
        this.moveHoverboard(this.langs.button);
      });
      this.langs.container.addEventListener('mouseleave', () => {
        this.toggleLangs('hide');
        this.hideHoverboard();
      });
    }

    // For login button
    this.login.addEventListener('mouseenter', (e) => {
      this.moveHoverboard(e.target);
    });
    this.login.addEventListener('mouseleave', () => {
      this.hideHoverboard();
    });

    this.menuData.dropdowns.forEach(dropdown => {
      dropdown.node.addEventListener('mouseenter', e => {
        const target = e.target.closest('.js-menu-dropdown');
        this.setActive(target);
        this.toggleSubmenu(target);
        this.toggleButtons(this.menuData.submenus[0].buttons[0].node);
        this.toggleSubmenuContainer('show');
        this.moveHoverboard(target.querySelector('.js-menu-button'));
      });
      dropdown.node.addEventListener('mouseleave', e => {
        const dropdown = e.target.closest('.js-menu-dropdown');
        const relatedTarget = e.relatedTarget;
        const hovered = relatedTarget ? relatedTarget.matches('.js-menu-container') || 
                        relatedTarget.closest('.js-menu-container') || 
                        relatedTarget.matches('.js-menu-dropdown') || 
                        relatedTarget.closest('.js-menu-dropdown') : null;
        const hoveredDropdown = relatedTarget ? relatedTarget.matches('.js-menu-dropdown') ||
                                relatedTarget.closest('.js-menu-dropdown') : null;
  
        if (hoveredDropdown) this.resetActive(dropdown);
        if (hovered) return
  
        this.hideHoverboard();
        this.resetActive(dropdown);
        this.toggleSubmenuContainer('hide');
      });
    });

    this.menuData.submenuContainer.addEventListener('mouseleave', () => {
      this.hideHoverboard();
      this.resetActive(this.menuData.dropdowns.find(dropdown => dropdown.index == this.menuData.submenus.find(submenu => submenu.isActive).index).node);
      this.toggleSubmenuContainer('hide');
    });

    this.menuData.submenus.forEach(submenu => {
      submenu.buttons.forEach(button => {
        button.node.addEventListener('mouseenter', (e) => {
          this.toggleButtons(e.target);
          this.toggleSubmenuContainer('resize');
        });
        button.node.addEventListener('mouseleave', (e) => {
          if (e.relatedTarget.matches('.js-submenu-buttons')) return
          this.resetActive(button.node);
          this.toggleSubmenuContainer('resize', true);
        });
        button.links.node.addEventListener('mouseenter', (e) => {
          this.setActive(button.node);
          this.toggleSubmenuContainer('resize');
        });
        button.links.node.addEventListener('mouseleave', (e) => {
          this.resetActive(button.node);
          this.toggleSubmenuContainer('resize', true);
        });
      });
    });
  }

  initMobileEvents = () => {
    this.menuData.dropdowns.forEach(dropdown => {
      dropdown.node.addEventListener('click', e => {
        const target = e.target.closest('.js-menu-dropdown');
        this.toggleSubmenuContainer('show');
        this.toggleMobileSubmenu(target);
      });
    });

    this.menuData.submenus.forEach(submenu => {
      submenu.buttons.forEach(button => {
        button.node.addEventListener('click', e => this.toggleMobileButtons(e.target.closest('.js-submenu-button')));
        button.links.backButton.addEventListener('click', e => this.toggleMobileButtons(e.target.closest('.js-links-back'), true))
      });
    });

    this.menuData.submenus.forEach(submenu => {
      const backButton = submenu.buttonsContainer.querySelector('.js-submenu-back');
      backButton.addEventListener('click', e => {
        this.toggleSubmenuContainer('hide');
      })
    })

    this.menuData.burger.addEventListener('click', e => {
      const burgersElement = [...this.menuData.burger.querySelectorAll('span')];
      const options = {
        duration: .3,
        ease: 'easeInOut'
      }
      if (this.menuData.burger.matches('.active')) {
        gsap.to(burgersElement[0], {rotate: 0,translateY: 0, ...options});
        gsap.to(burgersElement[1], {opacity: 1, ...options});
        gsap.to(burgersElement[2], {rotate: 0, translateY: 0, ...options});
        gsap.to(this.menuData.navContainer, {translateY: '-120%', ...options});
      } else {
        gsap.to(burgersElement[0], {rotate: 45, translateY: '5px', ...options});
        gsap.to(burgersElement[1], {opacity: 0, ...options});
        gsap.to(burgersElement[2], {rotate: -45, translateY: '-5px', ...options});
        gsap.to(this.menuData.navContainer, {translateY: 0, ...options});
      }
      this.menuData.burger.classList.toggle('active');
      document.body.classList.toggle('menu-opened');
    })

    this.closeButtons.forEach(closeButton => {
      closeButton.addEventListener('click', e => {
        this.resetMobileMenu(closeButton);
      })
    })

    if (this.langs) {
      this.header.appendChild(this.langs.list);

      this.langs.container.addEventListener('click', e => {
        this.toggleLangs('show');
      });

      this.langs.back.addEventListener('click', e => {
        this.toggleLangs('hide');
      });
    } 
  }
  
  initScrollEvent = () => {
    gsap.registerPlugin(ScrollTrigger);

    ScrollTrigger.create({
      id: 'headerTriger',
      trigger: this.header,
      start: "top+=1px top",
      end: () => `${document.body.scrollHeight}px`,
      pin: true,
      pinSpacing: false,
      scrub: true,
      onEnter: () => {
        if (this.screens.isTablet) {
          gsap.to(this.header, { top: 0 });
        } else {
          gsap.to(this.header, {
            borderRadius: "9999px",
            boxShadow: '0px 2px 2px 0px rgba(29, 36, 82, 0.08)',
            top: "8px",
            border: "1px solid #E5E9F1",
            margin: '0 auto',
            left: 8,
            width: "calc(100% - 16px)",
          });
        }
      },
      onLeaveBack: () => {
        if (this.screens.isTablet) return;
        gsap.to(this.header, {
          borderRadius: 0,
          border: 0,
          boxShadow: "none",
          width: "100%",
        });
      },
    });
  }

  setActive = (element) => {
    element.classList.add('active');
  }

  resetActive = (element) => {
    element.classList.remove('active');
  }

  resetMobileMenu = (closeButton) => {
    const parent = closeButton.parentElement;
    const backButton = parent.querySelector('.js-submenu-back') || parent.querySelector('.js-langs-back') || parent.querySelector('.js-links-back');
    if (backButton) {
      backButton.click();
      
      if (backButton.matches('.js-submenu-back') || backButton.matches('.js-langs-back')) {
        this.menuData.burger.click();
      } else {
        const submenuNode = this.menuData.submenus.find(submenu => submenu.isActive).node;
        closeButton = submenuNode.querySelector('.js-menu-close');
        this.resetMobileMenu(closeButton)
      }
    }
  }
  
  toggleLangs = (toggle) => {
    const options = {
      duration: .3,
      ease: 'easeInOut',
    };

    const toggleActions = {
      show: () => {
        gsap.to(this.langs.list, {
          visibility: 'visible',
          opacity: 1,
          translateX: 0,
          ...options,
        });
      },
      hide: () => {
        gsap.to(this.langs.list, {
          opacity: this.screens.isTablet ? 1 : 0,
          translateX: this.screens.isTablet ? '100%' : 0,
          ...options,
          onComplete: this.screens.isTablet ? null : () => {
            this.langs.list.style.visibility = 'hidden';
          },
        });
      },
    };

    toggleActions[toggle]();
  }

  toggleMobileSubmenu = (target) => {
    const activeIndex = target.getAttribute('data-submenu');
    const prevActiveSubmenu = this.menuData.submenus.find(submenu => submenu.isActive);
    this.menuData.submenus.forEach(submenu => submenu.isActive = submenu.index == activeIndex);
    const activeSubmenuNode = this.menuData.submenus.find(submenu => submenu.isActive).node;

    if (activeIndex == prevActiveSubmenu.index) {
      activeSubmenuNode.style.display = 'flex';
    } else {
      activeSubmenuNode.style.display = 'flex';
      prevActiveSubmenu.node.style.display = 'none';
    }
  }

  toggleSubmenu = (activeSubmenu) => {
    const activeIndex = activeSubmenu.getAttribute('data-submenu');
    const prevActiveSubmenu = this.menuData.submenus.find(submenu => submenu.isActive);
    this.menuData.submenus.forEach(submenu => submenu.isActive = submenu.index == activeIndex);
    const activeSubmenuNode = this.menuData.submenus.find(submenu => submenu.isActive).node;
    const animationSettings = {
      duration: .3,
      ease: 'easeInOut',
      overwrite: 'auto'
    };

    const animateSubmenu = (node, startX, endX, visibility = 'visible') => {
      node.style.display = 'flex';
      gsap.fromTo(node, 
        { x: startX },
        { opacity: 1, x: endX, visibility, ...animationSettings }
      );
    };

    const hidePrevSubmenu = (node, endX) => {
      gsap.fromTo(node, 
        { x: 0 },
        { opacity: 0, x: endX, ...animationSettings, onComplete: () => {
          node.style.visibility = 'hidden';
          node.style.display = 'none';
        } }
      );
    };

    if (activeIndex > prevActiveSubmenu.index) {
      animateSubmenu(activeSubmenuNode, '50%', 0);
      hidePrevSubmenu(prevActiveSubmenu.node, '-50%');
    } else if (activeIndex < prevActiveSubmenu.index) {
      animateSubmenu(activeSubmenuNode, '-50%', 0);
      hidePrevSubmenu(prevActiveSubmenu.node, '50%');
    } else {
      gsap.to(activeSubmenuNode, {
        opacity: 1,
        visibility: 'visible',
        display: 'flex',
        ...animationSettings
      });
    }
  }

  toggleSubmenuContainer = (status, hideLinks = false) => {
    const calculateHeight = () => {
      const activeSubmenu = this.menuData.submenus.find(submenu => submenu.isActive);
      const activeButton = activeSubmenu.buttons.find(button => button.isActive);
      return Math.max(activeSubmenu.height, hideLinks ? 0 : activeButton.links.height);
    }

    const calculateWidth = () => {
      const activeSubmenu = this.menuData.submenus.find(submenu => submenu.isActive);
      const activeButton = activeSubmenu.buttons.find(button => button.isActive);
      return activeSubmenu.buttonsWidth + (hideLinks ? 0 : activeButton.links.width);
    }

    const options = {
      duration: 0.3,
      ease: 'easeInOut'
    }

    switch (status) {
      case 'hide':
        if (this.screens.isTablet) {
          gsap.to(this.menuData.submenuContainer, {
            translateX: '100%',
            ...options
          });
        } else {
          gsap.to(this.menuData.submenuContainer, {
            opacity: '0',
            ...options,
            onComplete: () => {
              this.menuData.submenuContainer.style.display = 'none';
              this.menuData.submenuContainer.style.visibility = 'hidden';
            }
          });
        }
        break;
      case 'show':
        if (this.screens.isTablet) {
          gsap.to(this.menuData.submenuContainer, {
            translateX: 0,
            ...options
          });
        } else {
          gsap.to(this.menuData.submenuContainer, {
            opacity: '1',
            display: 'flex',
            visibility: 'visible',
            height: calculateHeight(),
            width: calculateWidth(),
            ...options
          });
        }
        break;
      case 'resize':
        gsap.to(this.menuData.submenuContainer, {
          height: calculateHeight(),
          width: calculateWidth(),
          duration: 0.3,
          ease: 'easeInOut'
        });
        break;
    }
  }

  toggleMobileButtons = (target, back = false) => {
    const activeSubmenu = this.menuData.submenus.find(submenu => submenu.isActive);
    const options = {
      duration: .3,
      ease: 'easeInOut',
    };

    if (back) {
      const prevActiveButton = activeSubmenu.buttons.find(button => button.isActive);
      prevActiveButton.isActive = false;
      gsap.to(activeSubmenu.node, {translateX: 0, ...options, onComplete: () => {
        prevActiveButton.links.node.style.display = 'none';
      }});
    } else {
      const activeButton = activeSubmenu.buttons.find(button => button.index == target.getAttribute('data-links'));
      activeButton.isActive = true;
      activeButton.links.node.style.display = 'flex';
      gsap.to(activeSubmenu.node, {translateX: '-100%', ...options});
    }
  }

  toggleButtons = (target) => {
    const activeSubmenu = this.menuData.submenus.find(submenu => submenu.isActive);
    const prevActiveButton = activeSubmenu.buttons.find(button => button.isActive);
    const activeButton = activeSubmenu.buttons.find(button => button.index == target.getAttribute('data-links'));
    prevActiveButton.isActive = false;
    activeButton.isActive = true;
    this.resetActive(prevActiveButton.node);
    this.setActive(activeButton.node);

    gsap.to(prevActiveButton.links.node, {
      opacity: '0',
      duration: 0.3,
      ease: 'easeInOut',
      onComplete: () => {
        prevActiveButton.links.node.style.visibility = 'hidden';
      }
    });

    gsap.to(activeButton.links.node, {
      opacity: '1',
      visibility: 'visible',
      duration: 0.3,
      ease: 'easeInOut',
    });
  }

  moveHoverboard = (target) => {
    const position = {
      left: target.getBoundingClientRect().left - this.header.getBoundingClientRect().left,
    }
    const size = {
      height: target.getBoundingClientRect().height,
      width: target.getBoundingClientRect().width,
    }

    this.hoverboard.classList.add('opacity-100');
    this.hoverboard.style.height = `${size.height}px`
    this.hoverboard.style.width = `${size.width}px`
    this.hoverboard.style.left = `${position.left}px`
  }

  hideHoverboard = () => {
    this.hoverboard.classList.remove('opacity-100');
  }
}